import axios from "axios";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useToastHelper } from "@/helpers/helperFunctions";
import { cn, countries, countries_iso2, countryData } from "@/lib/utils";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { NextPage } from "next";
import { useTranslation } from "next-i18next";
import { LuLoader2 } from "react-icons/lu";
import OTPInput from "react-otp-input";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsAndConditions from "@/components/TermsAndConditions";
import CustomerAgreement from "@/components/CustomerAgreement";
import { Checkbox } from "../ui/checkbox";
import Link from "next/link";

import clsx from "clsx";
import { AnalyticsEvents, useAnalytics } from "../hooks/useAnalytics";
import TurnstileWidget from "./TurnstileWidget";
import { FaArrowCircleRight } from "react-icons/fa";
import { usePostGoogleAuth } from "@/api/userApi";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePatternUniverses } from "@/contexts/PatternUniverse";
import { useUserContext } from "@/contexts/userContext";
import GoogleOAuthButton from "./GoogleOAuthButton";
interface IFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone_number: string;
  country: string;
  trading: string;
  university: string;
  referral: string;
  agreeMarketingCommunications: boolean;
}

const defaultFormValues: IFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phone_number: "",
  country: "",
  trading: "",
  university: "",
  referral: "",
  agreeMarketingCommunications: true,
};

const RegisterCard: NextPage = (props: any) => {
  const { toast } = useToastHelper();
  const formRef = useRef<HTMLFormElement | null>(null);
  const form = useForm<IFormValues>({
    defaultValues: defaultFormValues,
    mode: "onTouched",
  });
  const values = form.watch();
  const allFieldsFilled = Object.entries(values)
    .filter(([key]) => key !== "referral")
    .every(([, val]) => val !== undefined && val !== null && val !== "");
  const { data: session } = useSession();
  const router = useRouter();
  const ac = useRef<AbortController>();
  const { t } = useTranslation("common-page");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isReferDisabled, disableRefer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendOtpShow, setResendOtpShow] = useState(false);
  const [value, setValue] = useState<string>("United Kingdom");
  const [countryCode, setCountryCode] = useState<any>(
    countryData?.find((framework) => framework.name == value)?.dial_code || "",
  );
  const [show, setShow] = useState(false);
  const [otp, setOtp] = useState("");
  const togglePasswordVisibility = () => {
    if (showPassword) {
      setShowPassword(false);
    } else {
      setShowPassword(true);
    }
  };
  const [resendTimer, setResendTimer] = useState(0); // seconds left
  const [token, setToken] = useState<string>("");
  const { trackEvent } = useAnalytics();
  const { setSubscriptionData, setMetaData, getUserType } = useSubscription();
  const { refetchScannerStrategies, refetchUniverse } = usePatternUniverses();
  const { setUser, refreshConnectedBrokers } = useUserContext();
  const { mutateAsync: googleAuth, isPending: googleLoading } = usePostGoogleAuth();

  const onGoogleSuccess = async (idToken: string) => {
    try {
      setLoading(true);
      const res = await googleAuth({ idToken });
      if (res.status !== 200) throw new Error("Google registration failed");

      setSubscriptionData(res?.data?.subscription);
      setMetaData(res?.data?.user?.meta_data || null);
      getUserType(res.data);
      setUser(res.data);
      localStorage.setItem("userAuthenticated", "true");
      refetchScannerStrategies(true, res?.data?.user_id);
      refetchUniverse(true, res?.data?.user_id);
      refreshConnectedBrokers();

      toast.success("Registration successful");
      router.push(`/welcome-tour?locale=${router.query.locale || "en"}`);
    } catch (e: any) {
      const errorMsg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Google sign up failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setLoading(true);
    setResendTimer(60); // 60 seconds

    let url = new URL("https://hub2.trylevel2.com/send_sms_otp");
    url.searchParams.append("ph_number", "");
    url.searchParams.append("email", form.getValues("email"));
    url.searchParams.append("cf_token", token);
    try {
      const response = await axios.post(url.toString());
      if (response?.data?.message) {
        setShow(true);
        toast.success(response.data.message);
        trackEvent({
          event_name: AnalyticsEvents.REGISTRATION_CLICKED,
          params: { source: "login_page" },
        });
        setLoading(false);
      } else {
        toast.error("Error sending OTP.");
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? "Error sending OTP");
      setLoading(false);
    } finally {
      setLoading(false);
      (window as any)?.turnstile?.reset();
    }
  };
  const validSmsOtp = async (otp: any) => {
    setOtpLoading(true);
    let url = new URL("https://hub2.trylevel2.com/validate_sms_otp");
    url.searchParams.append("otp", otp);
    url.searchParams.append("email", form.getValues("email"));
    try {
      const res = await axios
        .post(url.toString())
        .catch((d) => {
          if (d?.response?.status === 400)
            toast.error(d?.response?.data?.detail);
          else toast.error("Error");
        })
        .finally(() => setOtpLoading(false));
      if (res?.data) {
        // check valid otp
        setShow(false);
        toast.success(res?.data?.message);
        try {
          ac.current?.abort?.();
          setLoading(true);
          const response = await axios
            .post(
              `https://hub2.trylevel2.com/register_user?auth=${res?.data.auth}`,
              {
                email: form.getValues("email"),
                password: form.getValues("password"),
                first_name: form.getValues("firstName"),
                last_name: form.getValues("lastName"),
                country: form.getValues("country"),
                trading_experience: form.getValues("trading"),
                phone_number: countryCode + form.getValues("phone_number"),
                referral: form.getValues("referral"),
                trading_challenge: "app.trylevel2.com",
                source: "new-site-reg-form",
                university: "",
              },
            )
            .catch((err) => {
              if (
                err?.response?.status === 400 &&
                err?.response?.data?.detail
              ) {
                toast.error(err.response.data.detail);
              } else throw err;
            });
          if (response) {
            router.push(response.data.redirect_url);
            toast.success(
              "Registration successful - Select the plan that best fits your needs",
            );
            trackEvent({
              event_name: AnalyticsEvents.REGISTRATION_SUCCESS,
              params: { source: "registration_page" }, // Optional metadata
            });
          }
          setLoading(false);
        } catch (error: any) {
          setOtpLoading(false);
          setLoading(false);
          toast.error(error.message);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? "Error Validating OTP");
      setLoading(false);
    } finally {
      setLoading(false);
      (window as any)?.turnstile?.reset();
    }
  };
  const syncFromDom = () => {
    if (!formRef.current) return;
    const fields: (keyof IFormValues)[] = [
      "firstName",
      "lastName",
      "email",
      "password",
      "referral",
    ];
    fields.forEach((fieldName) => {
      const el = formRef.current?.querySelector<HTMLInputElement>(
        `input[name="${fieldName}"]`,
      );
      if (el && typeof el.value === "string" && el.value.length > 0) {
        const currentValue = form.getValues(fieldName);
        if (el.value !== currentValue) {
          form.setValue(fieldName, el.value as any, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    });
  };

  useEffect(() => {
    // Run an autofill check shortly after mount when password managers populate the DOM
    const timer = setTimeout(() => {
      syncFromDom();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmitForm: SubmitHandler<IFormValues> = async (formData) => {
    sendOtp();
  };

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (router?.query?.referral) {
      form.setValue("referral", router.query.referral as string);
      disableRefer(true);
    }
  }, [session, router]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      form.setValue("agreeMarketingCommunications", true);
      if ("OTPCredential" in window) {
        ac.current = new AbortController();
        navigator.credentials
          .get({
            // @ts-ignore
            otp: { transport: ["sms"] },
            signal: ac.current.signal,
          })
          .then((otp) => {
            // @ts-ignore
            otp.code && setOtp(otp.code);
          })
          .catch((err) => {});
      }
    }
  }, []);

  const submit = () => {
    setOtp("");
    validSmsOtp(otp);
  };
  const resendTextAppear = () => {
    const interval = setInterval(() => setResendOtpShow(true), 60000);
    return () => {
      clearInterval(interval);
    };
  };
  const resend = () => {
    setResendOtpShow(false);

    sendOtp();
    resendTextAppear();
  };
  return (
    <>
      <Dialog open={show} onOpenChange={(open) => setShow(!show)}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Enter verification code</DialogTitle>
          </DialogHeader>
          <div className="w-full flex justify-center items-center h-full">
            <OTPInput
              value={otp}
              onChange={setOtp}
              shouldAutoFocus
              numInputs={4}
              renderSeparator={<span className="text-transparent">-</span>}
              renderInput={({
                style: { color, background, ...style },
                ...props
              }) => (
                <Input
                  {...props}
                  style={{
                    ...style,
                    color: void 0,
                    backgroundColor: void 0,
                    width: 40,
                  }}
                  autoComplete="off"
                />
              )}
            />
          </div>
          <div className="text-center text-muted-foreground text-sm">
            {t("A verification code has been sent to your email address")}
          </div>
          <div className="text-muted-foreground pt-2 text-center items-center flex flex-col">
            {t("Didn't receive OTP? ")}
            <p
              className={` w-56  ${
                resendTimer > 0
                  ? "cursor-not-allowed text-gray-400"
                  : "cursor-pointer hover:text-primary"
              }`}
              onClick={() => {
                if (resendTimer === 0) resend();
              }}
            >
              {resendTimer > 0
                ? `${t("Resend")} (${resendTimer}s)`
                : t("Resend")}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => submit()} disabled={loading}>
              {t("Submit")}{" "}
              {otpLoading ? <LuLoader2 className="animate-spin ml-1" /> : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-[700px] w-full mb-5">
        <GoogleOAuthButton
          onSuccess={onGoogleSuccess}
          isLoading={googleLoading || loading}
        />

        <div className="relative my-4 flex items-center justify-center w-full">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/60" />
          </div>
          <div className="relative bg-background px-3 text-xs uppercase text-muted-foreground font-medium">
            {t("Or register with email")}
          </div>
        </div>

        <Form {...form}>
          <form
            ref={formRef}
            method="post"
            autoComplete="on"
            onSubmit={(e) => {
              syncFromDom();
              form.handleSubmit(handleSubmitForm)(e);
            }}
            className="flex flex-col space-y-4 "
          >
            <div className="flex w-full gap-4 ">
              <FormField
                control={form.control}
                name="firstName"
                rules={{ required: t("First name is required") }}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        className={cn(
                          "w-full",
                          !field.value && "text-muted-foreground",
                        )}
                        placeholder={t("First name *")}
                        type="text"
                        autoComplete="given-name"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                rules={{ required: t("Last name is required") }}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        className="w-full"
                        placeholder={t("Last name *")}
                        type="text"
                        autoComplete="family-name"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: t("Email is required"),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t("Email is required"),
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      className={"w-full"}
                      placeholder={t("Email *")}
                      type="email"
                      autoComplete="email"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              rules={{ required: t("Password is required") }}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("Choose a password *")}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground cursor-pointer">
                        {showPassword ? (
                          <EyeOff
                            className="h-5 w-5"
                            onClick={togglePasswordVisibility}
                          />
                        ) : (
                          <Eye
                            className="h-5 w-5"
                            onClick={togglePasswordVisibility}
                          />
                        )}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            {/* Invite code field hidden as requested */}
            <input type="hidden" {...form.register("referral")} />
            <FormField
              control={form.control}
              name="agreeMarketingCommunications"
              disabled={isReferDisabled}
              rules={{
                required: false,
              }}
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="scale-75"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormDescription className="text-sm">
                      {t("Receive Level2 email updates")}
                    </FormDescription>
                  </div>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <TurnstileWidget
              onVerify={(token) => setToken(token)}
            ></TurnstileWidget>

            <div className="text-center flex justify-center w-full mt-4">
              <Button
                type="submit"
                disabled={loading}
                className={clsx(
                  "w-full flex items-center justify-center gap-2 text-sm sm:text-base font-semibold py-4 sm:py-6 transition-all duration-300 shadow-md",
                  !loading
                    ? "bg-[--accent-primary] text-white hover:bg-[--accent-primary]/90 hover:shadow-lg hover:shadow-[--accent-primary]/20 active:scale-[0.98]"
                    : "bg-[#254285] text-white/50 opacity-80 cursor-not-allowed",
                )}
              >
                <span>{t("Create Free Account")}</span>
                {loading ? (
                  <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                ) : (
                  <FaArrowCircleRight className="ml-2 h-5 w-5 transition-transform" />
                )}
              </Button>
            </div>

            <div className="flex flex-col gap-3 w-full text-center text-xs text-muted-foreground mt-4 pt-4 border-t border-muted/20">
              <p>
                {t("Have an account already?")}{" "}
                <Link
                  href="/"
                  className="cursor-pointer select-none font-semibold underline underline-offset-4 hover:text-[--accent-primary]/80 transition-colors text-[--accent-primary]"
                >
                  {t("Sign In")}
                </Link>
              </p>

              <div className="text-[11px] leading-relaxed text-muted-foreground/80 space-y-1.5 px-4">
                <p>
                  {t("By creating an account, you acknowledge and accept our")}{" "}
                  <TermsAndConditions />
                  {","} <PrivacyPolicy /> {t("and")} <CustomerAgreement />
                </p>

                <p className="text-[10px] text-muted-foreground/60">
                  {t("Need tech support? Email us at")}{" "}
                  <a
                    href="mailto:contact@trylevel2.com"
                    className="cursor-pointer select-none underline underline-offset-4 hover:text-[--accent-primary]/80 transition-colors text-[--accent-primary]"
                  >
                    contact@trylevel2.com
                  </a>
                </p>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
};

export default RegisterCard;
